import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { User, Calendar, Star, Package, LogOut, Plus, Edit3, MapPin, Phone, Mail, ChevronRight, Upload, ImagePlus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-blue-50 text-blue-700 border-blue-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    processing: 'bg-[var(--color-sage-soft)] text-[var(--color-sage)] border-[rgba(77,122,101,0.3)]'
  };
  const labels = { pending: 'In attesa', confirmed: 'Confermato', completed: 'Completato', cancelled: 'Annullato', processing: 'In lavorazione' };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {labels[status] || status}
    </span>
  );
};

export default function ClientDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [memorials, setMemorials] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingMemorials, setLoadingMemorials] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    city: user?.city || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // New memorial dialog
  const [memorialOpen, setMemorialOpen] = useState(false);
  const [memorialForm, setMemorialForm] = useState({
    pet_name: '', species: 'cane', breed: '', birth_date: '', death_date: '', description: '', is_public: true
  });
  const [savingMemorial, setSavingMemorial] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [memorialPhotoPreview, setMemorialPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get('/bookings/mine').then(r => setBookings(r.data)).catch(() => {}).finally(() => setLoadingBookings(false));
    api.get('/memorials/mine').then(r => setMemorials(r.data)).catch(() => {}).finally(() => setLoadingMemorials(false));
    api.get('/orders/mine').then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoadingOrders(false));
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await api.put('/auth/profile', profileForm);
      updateUser(res.data);
      toast.success('Profilo aggiornato!');
    } catch {
      toast.error('Errore nel salvataggio del profilo');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Immagine troppo grande (max 5MB)'); return; }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMemorialForm(p => ({ ...p, photo_url: res.data.url }));
      setMemorialPhotoPreview(URL.createObjectURL(file));
      toast.success('Foto caricata!');
    } catch {
      toast.error('Errore nel caricamento della foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCreateMemorial = async (e) => {
    e.preventDefault();
    setSavingMemorial(true);
    try {
      const res = await api.post('/memorials', memorialForm);
      setMemorials(prev => [res.data, ...prev]);
      setMemorialOpen(false);
      setMemorialForm({ pet_name: '', species: 'cane', breed: '', birth_date: '', death_date: '', description: '', is_public: true });
      setMemorialPhotoPreview(null);
      toast.success('Memorial creato!');
    } catch {
      toast.error('Errore nella creazione del memorial');
    } finally {
      setSavingMemorial(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success(t('auth.logout_success'));
    navigate('/');
  };

  const speciesEmoji = { cane: '🐕', gatto: '🐈', coniglio: '🐇', uccello: '🐦', altro: '🐾' };

  return (
    <div className="min-h-screen bg-[var(--color-cream)]" data-testid="client-dashboard">
      {/* Header banner */}
      <div className="bg-[var(--color-deep)] text-white py-10">
        <div className="container-custom">
          <motion.div {...fadeUp} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[var(--color-sage)] flex items-center justify-center">
                <User size={28} className="text-white" />
              </div>
              <div>
                <p className="text-[var(--color-stone)] text-sm">{t('dashboard.welcome', { name: '' }).replace(', {{name}}', '')}</p>
                <h1 className="font-serif text-2xl font-semibold">{user?.first_name} {user?.last_name}</h1>
                <p className="text-[var(--color-stone)] text-xs mt-0.5">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-[var(--color-stone)] hover:text-white hover:bg-white/10 gap-2"
              onClick={handleLogout}
              data-testid="dashboard-logout-button"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-10">
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-xl p-1 mb-8 flex-wrap h-auto gap-1">
            <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-[var(--color-sage)] data-[state=active]:text-white gap-2" data-testid="tab-bookings">
              <Calendar size={15} /> {t('dashboard.bookings')}
            </TabsTrigger>
            <TabsTrigger value="memorials" className="rounded-lg data-[state=active]:bg-[var(--color-sage)] data-[state=active]:text-white gap-2" data-testid="tab-memorials">
              <Star size={15} /> {t('dashboard.memorials')}
            </TabsTrigger>
            <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-[var(--color-sage)] data-[state=active]:text-white gap-2" data-testid="tab-orders">
              <Package size={15} /> {t('dashboard.orders')}
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-[var(--color-sage)] data-[state=active]:text-white gap-2" data-testid="tab-profile">
              <User size={15} /> {t('dashboard.profile')}
            </TabsTrigger>
          </TabsList>

          {/* PRENOTAZIONI */}
          <TabsContent value="bookings" data-testid="client-bookings-card">
            {loadingBookings ? (
              <div className="space-y-4">
                {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-16 bg-[var(--color-paper)] rounded-2xl border border-[var(--color-border)]">
                <Calendar size={40} className="mx-auto text-[var(--color-stone)] mb-4" />
                <h3 className="font-serif text-xl text-[var(--color-deep)] mb-2">{t('dashboard.no_bookings')}</h3>
                <p className="text-[var(--color-muted-text)] text-sm mb-6">Prenota un servizio e gestisci il tutto da qui</p>
                <Button className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl" onClick={() => navigate('/servizi')} data-testid="book-service-button">
                  {t('dashboard.book_service')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map(booking => (
                  <motion.div key={booking.id} {...fadeUp}
                    className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-5 flex items-start justify-between gap-4"
                    data-testid={`booking-item-${booking.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-serif font-semibold text-[var(--color-deep)] truncate">{booking.service_name_it}</h4>
                        <StatusBadge status={booking.status} />
                      </div>
                      <p className="text-sm text-[var(--color-muted-text)]">Animale: <span className="text-[var(--color-ink)] font-medium">{booking.pet_name}</span> ({booking.pet_species})</p>
                      {booking.preferred_date && <p className="text-xs text-[var(--color-stone)] mt-1">Data preferita: {booking.preferred_date}</p>}
                      <p className="text-xs text-[var(--color-stone)] mt-1">{new Date(booking.created_at).toLocaleDateString('it-IT')}</p>
                    </div>
                    {booking.admin_notes && (
                      <div className="bg-[var(--color-sage-soft)] text-[var(--color-sage)] text-xs rounded-xl px-3 py-2 max-w-xs">
                        {booking.admin_notes}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* MEMORIAL */}
          <TabsContent value="memorials" data-testid="client-memorials-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl font-semibold text-[var(--color-deep)]">{t('dashboard.memorials')}</h2>
              <Dialog open={memorialOpen} onOpenChange={setMemorialOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl gap-2" data-testid="create-memorial-button">
                    <Plus size={16} /> {t('dashboard.create_memorial')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[var(--color-paper)] border-[var(--color-border)] sm:max-w-lg rounded-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl text-[var(--color-deep)]">Crea un Memorial</DialogTitle>
                    <DialogDescription className="text-[var(--color-muted-text)] text-sm">
                      Crea un profilo memoriale per il tuo compagno nel Giardino delle Stelle.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateMemorial} className="space-y-4 mt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-sm">Nome del pet *</Label>
                        <Input value={memorialForm.pet_name} onChange={e => setMemorialForm(p => ({...p, pet_name: e.target.value}))} placeholder="Luna" required className="rounded-xl" data-testid="memorial-name-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Specie *</Label>
                        <Select value={memorialForm.species} onValueChange={v => setMemorialForm(p => ({...p, species: v}))}>
                          <SelectTrigger className="rounded-xl" data-testid="memorial-species-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cane">Cane</SelectItem>
                            <SelectItem value="gatto">Gatto</SelectItem>
                            <SelectItem value="coniglio">Coniglio</SelectItem>
                            <SelectItem value="uccello">Uccello</SelectItem>
                            <SelectItem value="altro">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Razza</Label>
                      <Input value={memorialForm.breed} onChange={e => setMemorialForm(p => ({...p, breed: e.target.value}))} placeholder="Opzionale" className="rounded-xl" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-sm">Data di nascita</Label>
                        <Input type="date" value={memorialForm.birth_date} onChange={e => setMemorialForm(p => ({...p, birth_date: e.target.value}))} className="rounded-xl" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Data del decesso</Label>
                        <Input type="date" value={memorialForm.death_date} onChange={e => setMemorialForm(p => ({...p, death_date: e.target.value}))} className="rounded-xl" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Descrizione</Label>
                      <Textarea value={memorialForm.description} onChange={e => setMemorialForm(p => ({...p, description: e.target.value}))} placeholder="Racconta la storia del tuo compagno..." rows={3} className="rounded-xl" data-testid="memorial-description-input" />
                    </div>
                    {/* Foto del memorial */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">Foto del tuo amico</Label>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        ref={photoInputRef}
                        className="hidden"
                        onChange={handlePhotoUpload}
                        data-testid="memorial-photo-input"
                      />
                      {memorialPhotoPreview ? (
                        <div className="relative w-full h-32 rounded-xl overflow-hidden border border-[var(--color-border)]">
                          <img src={memorialPhotoPreview} alt="Anteprima" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => { setMemorialPhotoPreview(null); setMemorialForm(p => ({...p, photo_url: ''})); }}
                            className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition-colors"
                            data-testid="memorial-photo-remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploadingPhoto}
                          className="w-full h-24 border-2 border-dashed border-[var(--color-border)] rounded-xl flex flex-col items-center justify-center gap-2 text-[var(--color-muted-text)] hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] hover:bg-[var(--color-sage-soft)] transition-colors cursor-pointer"
                          data-testid="memorial-photo-upload-btn"
                        >
                          {uploadingPhoto ? (
                            <span className="text-xs">Caricamento...</span>
                          ) : (
                            <>
                              <ImagePlus size={22} />
                              <span className="text-xs font-medium">Clicca per aggiungere una foto</span>
                              <span className="text-[10px] text-[var(--color-stone)]">JPG, PNG, WEBP — max 5MB</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <Button type="submit" disabled={savingMemorial || uploadingPhoto} className="w-full bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl" data-testid="memorial-submit-button">
                      {savingMemorial ? 'Creazione...' : uploadingPhoto ? 'Caricamento foto...' : 'Crea Memorial'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loadingMemorials ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3].map(i => <Skeleton key={i} className="h-52 rounded-2xl" />)}
              </div>
            ) : memorials.length === 0 ? (
              <div className="text-center py-16 bg-[var(--color-paper)] rounded-2xl border border-[var(--color-border)]">
                <Star size={40} className="mx-auto text-[var(--color-stone)] mb-4" />
                <h3 className="font-serif text-xl text-[var(--color-deep)] mb-2">{t('dashboard.no_memorials')}</h3>
                <p className="text-[var(--color-muted-text)] text-sm">Crea il primo profilo memorial per il tuo compagno</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {memorials.map(m => (
                  <Link key={m.id} to={`/giardino-delle-stelle/${m.id}`}
                    className="card-hover bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden block group"
                    data-testid={`memorial-item-${m.id}`}
                  >
                    {m.photo_url && (
                      <div className="h-36 overflow-hidden">
                        <img src={m.photo_url} alt={m.pet_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-serif font-semibold text-[var(--color-deep)]">{m.pet_name} {speciesEmoji[m.species] || '🐾'}</h4>
                        <span className="text-xs badge-cream px-2 py-0.5 rounded-full capitalize">{m.species}</span>
                      </div>
                      {m.breed && <p className="text-xs text-[var(--color-muted-text)]">{m.breed}</p>}
                      <div className="flex items-center gap-1 mt-2 text-xs text-[var(--color-muted-text)]">
                        <span className="text-[var(--color-terracotta)]">♥</span>
                        <span className="font-medium">{m.candles}</span> candele
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ORDINI */}
          <TabsContent value="orders">
            {loadingOrders ? (
              <div className="space-y-4">
                {[1,2].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-[var(--color-paper)] rounded-2xl border border-[var(--color-border)]">
                <Package size={40} className="mx-auto text-[var(--color-stone)] mb-4" />
                <h3 className="font-serif text-xl text-[var(--color-deep)] mb-2">{t('dashboard.no_orders')}</h3>
                <p className="text-[var(--color-muted-text)] text-sm mb-6">Visita il nostro shop per trovare oggetti memoriali</p>
                <Button className="bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl" onClick={() => navigate('/shop')}>
                  Visita lo shop
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <motion.div key={order.id} {...fadeUp}
                    className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-5"
                    data-testid={`order-item-${order.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-serif font-semibold text-[var(--color-deep)]">Ordine #{order.id.slice(0, 8)}</h4>
                          <StatusBadge status={order.status} />
                        </div>
                        <div className="text-sm text-[var(--color-muted-text)]">
                          {Array.isArray(order.items) && order.items.map((item, i) => (
                            <span key={i}>{item.product_name} (x{item.quantity}){i < order.items.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                        <p className="text-xs text-[var(--color-stone)] mt-1">{new Date(order.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PROFILO */}
          <TabsContent value="profile">
            <div className="max-w-lg">
              <h2 className="font-serif text-xl font-semibold text-[var(--color-deep)] mb-6">{t('dashboard.profile')}</h2>
              <form onSubmit={handleSaveProfile} className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-6 space-y-4" data-testid="profile-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">{t('auth.first_name')}</Label>
                    <Input value={profileForm.first_name} onChange={e => setProfileForm(p => ({...p, first_name: e.target.value}))} className="rounded-xl" data-testid="profile-first-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">{t('auth.last_name')}</Label>
                    <Input value={profileForm.last_name} onChange={e => setProfileForm(p => ({...p, last_name: e.target.value}))} className="rounded-xl" data-testid="profile-last-name" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2"><Phone size={14} />{t('contact.phone')}</Label>
                  <Input value={profileForm.phone || ''} onChange={e => setProfileForm(p => ({...p, phone: e.target.value}))} placeholder="+39 333 1234567" className="rounded-xl" data-testid="profile-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium flex items-center gap-2"><MapPin size={14} />Citt&agrave;</Label>
                  <Input value={profileForm.city || ''} onChange={e => setProfileForm(p => ({...p, city: e.target.value}))} placeholder="Milano" className="rounded-xl" data-testid="profile-city" />
                </div>
                <div className="pt-1">
                  <div className="flex items-center gap-2 text-sm text-[var(--color-muted-text)] mb-4">
                    <Mail size={14} />
                    <span>{user?.email}</span>
                    <span className="badge-sage text-xs px-2 py-0.5 rounded-full">Email confermata</span>
                  </div>
                </div>
                <Button type="submit" disabled={savingProfile} className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl" data-testid="profile-save-button">
                  {savingProfile ? 'Salvataggio...' : t('common.save')}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
